import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import { AdminPanelSettings, ArrowBack } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { superAdminAuthService } from '../../../services/superAdmin/superAdminAuthService';

const OtpContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
}));

const OtpCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(5),
  boxShadow: '0 20px 60px rgba(20, 184, 166, 0.2)',
  border: '1px solid rgba(20, 184, 166, 0.2)',
  maxWidth: '420px',
  width: '100%',
  margin: theme.spacing(2)
}));

const OtpInput = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    fontSize: '2rem',
    fontWeight: 700,
    letterSpacing: '0.5em',
    textAlign: 'center',
    '& input': { textAlign: 'center' }
  }
}));

const SuperAdminOtpVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const { email, password } = location.state || {};

  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/super-admin/login');
    }
    inputRef.current?.focus();
  }, [email, navigate]);

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown(resendCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError('Please enter the 6-digit OTP.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await superAdminAuthService.verifyOtp({ email, password, otp });
      if (result.success) {
        navigate('/super-admin');
      } else {
        setError(result.message || 'Invalid OTP.');
      }
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');
    try {
      const result = await superAdminAuthService.resendOtp(email);
      if (result.success) {
        setSuccess('OTP resent successfully.');
        setResendCountdown(60);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to resend OTP.');
      }
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  return (
    <OtpContainer>
      <OtpCard>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <AdminPanelSettings sx={{ fontSize: 48, color: '#14b8a6', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Verify OTP
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Enter the code sent to {email}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

        <form onSubmit={handleVerify}>
          <OtpInput
            fullWidth
            value={otp}
            onChange={handleOtpChange}
            inputRef={inputRef}
            placeholder="000000"
            sx={{ mb: 3 }}
          />
          <EasyCheckButton
            type="submit"
            fullWidth
            disabled={loading || otp.length !== 6}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#14b8a6',
              '&:hover': { backgroundColor: '#0d9488' },
              fontWeight: 600
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </EasyCheckButton>
        </form>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          {resendCountdown > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Resend in {resendCountdown}s
            </Typography>
          ) : (
            <Link
              component="button"
              onClick={handleResend}
              disabled={resendLoading}
              sx={{ color: '#14b8a6', cursor: 'pointer' }}
            >
              {resendLoading ? 'Sending...' : 'Resend OTP'}
            </Link>
          )}
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link
            component="button"
            onClick={() => navigate('/super-admin/login')}
            sx={{ color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}
          >
            <ArrowBack fontSize="small" /> Back to login
          </Link>
        </Box>
      </OtpCard>
    </OtpContainer>
  );
};

export default SuperAdminOtpVerify;
